import type { SetValues, Values } from 'nuqs'
import {
  createParser,
  createSerializer,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs'
import type { components } from '@joyful/openapi/types.gen'
import { E164PhoneNumberSchema, createEnumSchema } from '@joyful/utils'

export const PhoneQueueFilterTabs = [
  'active',
  'backlog',
  'needs_review',
  'completed',
  'failed',
] as const

export type QueueFilterTab = (typeof PhoneQueueFilterTabs)[number]

export const PhoneQueueQueryMap = {
  page: parseAsInteger.withDefault(1),
  qf: parseAsStringLiteral(PhoneQueueFilterTabs).withDefault('active'),
}

const PhoneRouteQueryMap = {
  dial: parseAsBoolean.withDefault(false),
  settings: parseAsBoolean.withDefault(false),
  userId: parseAsString,
}

const PhoneCallsRouteQueryMap = {
  ...PhoneRouteQueryMap,
  callId: parseAsString,
}

export function usePhoneCallsRouteQueryParams() {
  return useQueryStates(PhoneCallsRouteQueryMap)
}

export const serializePhoneCallsRouteQueryParams = createSerializer(
  PhoneCallsRouteQueryMap,
)

const parseAsE164PhoneNumber = createParser({
  parse: (value) => {
    return E164PhoneNumberSchema.safeParse(value).data ?? null
  },
  serialize: (value) => {
    return value
  },
})

export const PhoneHistoryQueryMap = {
  limit: parseAsInteger.withDefault(50),
  page: parseAsInteger.withDefault(1),
  phoneNumber: parseAsE164PhoneNumber,
  userId: parseAsString,
}

export type PhoneHistoryQueryParams = Values<typeof PhoneHistoryQueryMap>

export type SetPhoneHistoryQueryParams = SetValues<typeof PhoneHistoryQueryMap>

export const EmptyPhoneHistoryQueryParams: PhoneHistoryQueryParams = {
  limit: 50,
  page: 1,
  phoneNumber: null,
  userId: null,
}

export function usePhoneHistoryQueryParams() {
  return useQueryStates(PhoneHistoryQueryMap, { history: 'push' })
}

const PhoneMessageRouteQueryMap = {
  ...PhoneRouteQueryMap,
  messageId: parseAsString,
}

export function usePhoneMessageRouteQueryParams() {
  return useQueryStates(PhoneMessageRouteQueryMap)
}

const PayerPhoneDirectoryRouteQueryMap = {
  ...PhoneRouteQueryMap,
  callsPage: parseAsInteger.withDefault(1),
  create: parseAsBoolean.withDefault(false),
  payerPhoneDirectoryId: parseAsString,
}

export function usePayerPhoneDirectoryRouteQueryParams() {
  return useQueryStates(PayerPhoneDirectoryRouteQueryMap)
}

export const PayerPhoneDirectorySortFieldSchema = createEnumSchema<
  components['schemas']['PayerPhoneDirectorySortField']
>([
  { title: 'Name', value: 'name' },
  { title: 'Phone number', value: 'phone_number' },
  { title: 'Created', value: 'created_at' },
])

export const PayerPhoneDirectoryViewSchema = createEnumSchema([
  { title: 'All', value: 'all' },
  { title: 'Callable', value: 'callable' },
  { title: 'Do not call', value: 'do_not_call' },
])

const PayerPhoneDirectoryPaginationQueryMap = {
  do_not_call_view: parseAsStringLiteral(
    PayerPhoneDirectoryViewSchema.values(),
  ).withDefault('all'),
  limit: parseAsInteger.withDefault(50),
  page: parseAsInteger.withDefault(1),
  query: parseAsString.withDefault(''),
  sort_field: parseAsStringLiteral(
    PayerPhoneDirectorySortFieldSchema.values(),
  ).withDefault('name'),
  sort_order: parseAsStringLiteral(['asc', 'desc'] as const).withDefault('asc'),
}

export function usePayerPhoneDirectoryPagination() {
  const [payerPhoneDirectoryPagination, setPayerPhoneDirectoryPagination] =
    useQueryStates(PayerPhoneDirectoryPaginationQueryMap, { history: 'push' })

  return {
    payerPhoneDirectoryPagination,
    setPayerPhoneDirectoryPagination,
  }
}
